import { Injectable, Inject } from '@angular/core';

import { Observable, of, Subscription } from 'rxjs';

import { LOCAL_STORAGE, SESSION_STORAGE } from '@app/core/services/storage';
import { AuthService } from '@app/core/services/auth.service';
import { LogService } from '@app/core/services/log.service';

import { GridSettings } from '../interface/grid-settings.interface';
import { EphemeralStorage } from '@app/core/services/ephemeralStorage';

/*
 * Stores Kendo Grid UI Settings per instance and per Sid
 */
export interface IKendoGridSettingsStorageService {
	get(gridInstance: string): GridSettings;
	set(gridInstance: string, gridConfig: GridSettings): void;
}

@Injectable({
	providedIn: 'root',
})
export class KendoGridSettingsStorageService extends EphemeralStorage implements IKendoGridSettingsStorageService {
	/*
	 * Stores Kendo Grid UI Settings per instance and per Sid
	 */
	constructor(
		private readonly authService: AuthService,
		private readonly log: LogService,
		@Inject(SESSION_STORAGE) storage: Storage
	) {
		super('Kpa.KendoGridSettings', storage);
	}

	private userChange$: Subscription;

	/**
	 * @description attempt to retrieve from cache or return null
	 * @param gridInstance grid instance to retrieve settings for
	 */
	get(gridInstance): GridSettings {
		if (this.initialized === false || !gridInstance) {
			this.log.logVerbose('KendoGridSettingsStorageService: Not retrieved. storage not set up.');
			// short out consistent with value not found so app continues to run.
			return undefined;
		}

		const cached = this.getItemAs<GridSettings>(gridInstance);

		if (!cached) {
			return undefined;
		}

		return cached;
	}

	/**
	 * @description store the settings per instance for user session
	 * @param gridInstance
	 * @param gridSettings
	 */
	set(gridInstance: string, gridSettings: GridSettings): void {
		if (this.initialized && gridInstance) {
			try {
				this.setItem(gridInstance, gridSettings);
			} catch (eSet) {
				if (eSet.code) {
					if (eSet.code === DOMException.QUOTA_EXCEEDED_ERR) {
						this.log.logVerbose('KendoGridSettingsStorageService: storage full.');
					}
				} else {
					this.log.logVerbose('KendoGridSettingsStorageService: value not set cause of exception.');
					throw eSet;
				}
			}
		} else {
			// app continues to run though caching not working.
			this.log.logVerbose('KendoGridSettingsStorageService: value not set.');
		}
	}
}
